import React, { useState, useMemo } from "react";
import {
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import SyncIcon from "@mui/icons-material/Sync";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import {
  SharePointHub,
  FileVersionHistoryDrawer,
  SimilarFilesDialog,
  SyncStatusDashboard,
  DeletedFilesLog,
} from "../components/SharePoint";

const POPPINS = { fontFamily: "'Poppins', sans-serif" };

// Base tabs always visible to every user
const BASE_TABS = [
  { label: "Files",   icon: <FolderOpenIcon fontSize="small" />,   key: "files" },
  { label: "Deleted", icon: <DeleteOutlineIcon fontSize="small" />, key: "deleted" },
];

// Admin-only tab appended at the end when platform_admin=true
const ADMIN_TAB = { label: "Sync Status", icon: <SyncIcon fontSize="small" />, key: "sync" };

function a11yProps(index) {
  return { id: `sp-tab-${index}`, "aria-controls": `sp-tabpanel-${index}` };
}

/**
 * SharePointPage — full-page tabbed container hosting all SharePoint-related
 * views.  Mounted at /sharepoint and also reachable from the sidebar menu.
 *
 * Tab 0 — Files:       SharePointHub (paginated file table)
 * Tab 1 — Deleted:     DeletedFilesLog (reconciliation audit trail)
 * Tab 2 — Sync Status: SyncStatusDashboard — only shown when ?platform_admin=true
 *
 * Modals / drawers (version history, similar files) are rendered here so
 * they can overlay any tab without re-mounting child components.
 */
const SharePointPage = () => {
  const [activeTab, setActiveTab] = useState(0);

  // Detect ?platform_admin=true in the URL (checked once on mount)
  const isPlatformAdmin = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("platform_admin") === "true";
  }, []);

  const TAB_DEFS = useMemo(
    () => (isPlatformAdmin ? [...BASE_TABS, ADMIN_TAB] : BASE_TABS),
    [isPlatformAdmin]
  );

  // Version history drawer state
  const [versionDrawerOpen, setVersionDrawerOpen] = useState(false);
  const [versionFile, setVersionFile] = useState(null);

  // Similar files dialog state
  const [similarDialogOpen, setSimilarDialogOpen] = useState(false);
  const [similarFile, setSimilarFile] = useState(null);

  const handleViewVersions = (file) => {
    setVersionFile(file);
    setVersionDrawerOpen(true);
  };

  const handleFindSimilar = (file) => {
    setSimilarFile(file);
    setSimilarDialogOpen(true);
  };

  // "Use in Chat" from similar files: navigate back to Home and inject the file
  // as a document filter.  Uses a CustomEvent so MainPage can pick it up without
  // tight coupling.
  const handleUseInChat = (similarResult) => {
    window.dispatchEvent(
      new CustomEvent("sharepoint:useInChat", {
        detail: { filename: similarResult.filename, userId: similarResult.user_id },
      })
    );
    setSimilarDialogOpen(false);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Page header */}
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 0,
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#fff",
          flexShrink: 0,
        }}
      >
          {/* <Typography
            variant="h6"
            sx={{ ...POPPINS, fontWeight: 700, color: HEADER_COLOR, mb: 1 }}
          >
            CHIP Repository Hub
          </Typography> */}

        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTab-root": {
              ...POPPINS,
              fontSize: "0.82rem",
              fontWeight: 600,
              textTransform: "none",
              minHeight: 44,
            },
          }}
        >
          {TAB_DEFS.map((t, i) => (
            <Tab
              key={t.label}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  {t.icon}
                  {t.label}
                </Box>
              }
              {...a11yProps(i)}
            />
          ))}
        </Tabs>
      </Box>

      {/* Tab content — overflow-y scroll per panel */}
      <Box sx={{ flex: 1, overflow: "auto", backgroundColor: "#fafbfd" }}>
        {/* Files tab */}
        <Box
          role="tabpanel"
          hidden={activeTab !== TAB_DEFS.findIndex((t) => t.key === "files")}
          id="sp-tabpanel-files"
          aria-labelledby="sp-tab-files"
        >
          {activeTab === TAB_DEFS.findIndex((t) => t.key === "files") && (
            <SharePointHub
              onViewVersions={handleViewVersions}
              onFindSimilar={handleFindSimilar}
            />
          )}
        </Box>

        {/* Deleted tab */}
        <Box
          role="tabpanel"
          hidden={activeTab !== TAB_DEFS.findIndex((t) => t.key === "deleted")}
          id="sp-tabpanel-deleted"
          aria-labelledby="sp-tab-deleted"
        >
          {activeTab === TAB_DEFS.findIndex((t) => t.key === "deleted") && <DeletedFilesLog />}
        </Box>

        {/* Sync Status tab — admin only */}
        {isPlatformAdmin && (
          <Box
            role="tabpanel"
            hidden={activeTab !== TAB_DEFS.findIndex((t) => t.key === "sync")}
            id="sp-tabpanel-sync"
            aria-labelledby="sp-tab-sync"
          >
            {activeTab === TAB_DEFS.findIndex((t) => t.key === "sync") && <SyncStatusDashboard />}
          </Box>
        )}
      </Box>

      {/* Overlays — mounted once, driven by state */}
      <FileVersionHistoryDrawer
        open={versionDrawerOpen}
        file={versionFile}
        onClose={() => setVersionDrawerOpen(false)}
      />

      <SimilarFilesDialog
        open={similarDialogOpen}
        file={similarFile}
        onClose={() => setSimilarDialogOpen(false)}
        onUseInChat={handleUseInChat}
      />
    </Box>
  );
};

export default SharePointPage;
